import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useMutation, useQuery } from "react-query";
import { useIntegrationApi } from "@/api/integration/Integration.api";
import { useApi } from "@/api/useApi";
import {
  ArticleCategory,
  ZendeskConfigurationRequest,
} from "@/api/integration/Integration.model";
import { toast } from "sonner";
import { useQueries } from "react-query";
import { useMemo } from "react";

type BrandsCategoriesSelection = Record<string, string[]>;

export interface UseArticleCategorySelectorProps {
  source: string;
  onClose: () => void;
}

export function useArticleCategorySelector({
  source,
  onClose,
}: UseArticleCategorySelectorProps) {
  const { api } = useApi();
  const {
    updateSelectedArticleCategories: updateCategories,
    getArticleCategories: getArticleCategories,
    getSelectedCategories: getSelectedCategories,
    getBrandsConfiguration: getBrandsConfiguration,
  } = useIntegrationApi(api);

  const { data: tenantBrands, isLoading: isLoadingBrands } = useQuery({
    queryKey: ["tenantBrands"],
    queryFn: async () => await getBrandsConfiguration(),
  });

  // State for managing categories per brand
  const [selection, setSelection] = useState<BrandsCategoriesSelection>({});

  const [isSaving, setIsSaving] = useState(false);

  // Get article categories for each brand - using a single query with dynamic data fetching
  const brandArticleCategoriesQueries = useQueries(
    (tenantBrands ?? []).map((brand) => ({
      queryKey: ["getArticleCategories", source, brand.subdomain],
      queryFn: async () => {
        const categories = await getArticleCategories(source, brand.subdomain);
        return { brandName: brand.subdomain, categories };
      },
      enabled: !!tenantBrands && tenantBrands.length > 0,
    }))
  );

  const allBrandCategories = useMemo(() => {
    if (!tenantBrands || tenantBrands.length === 0) return {};
    const categoryMap: { [brandName: string]: ArticleCategory[] } = {};
    brandArticleCategoriesQueries.forEach((query, idx) => {
      const brand = tenantBrands[idx];
      if (brand && query.data) {
        categoryMap[brand.subdomain] = query.data.categories;
      }
    });
    return categoryMap;
  }, [brandArticleCategoriesQueries, tenantBrands]);

  const isLoadingCategories = useMemo(() => {
    return brandArticleCategoriesQueries.some((q) => q.isLoading);
  }, [brandArticleCategoriesQueries]);

  const selectedCategoriesQueries = useQueries(
    (tenantBrands ?? []).map((brand) => ({
      queryKey: ["getSelectedCategories", source, brand.subdomain],
      queryFn: async () => {
        const selected = await getSelectedCategories(source, brand.subdomain);
        return { brandName: brand.subdomain, selected };
      },
      enabled: !!tenantBrands && tenantBrands.length > 0,
      onSuccess: (data: { brandName: string; selected: ArticleCategory[] }) => {
        setSelection((prev) => {
          return {
            ...prev,
            [data.brandName]: data.selected.map((category) =>
              String(category.id)
            ),
          };
        });
      },
    }))
  );

  const isLoadingSelectedCategories = useMemo(() => {
    return selectedCategoriesQueries.some((q) => q.isLoading);
  }, [selectedCategoriesQueries]);

  const allSelectedCategories = useMemo(() => {
    if (!tenantBrands || tenantBrands.length === 0) return {};
    const selectedMap: { [brandName: string]: ArticleCategory[] } = {};
    selectedCategoriesQueries.forEach((query, idx) => {
      const brand = tenantBrands[idx];
      if (brand && query.data) {
        selectedMap[brand.subdomain] = query.data.selected;
      }
    });
    return selectedMap;
  }, [selectedCategoriesQueries, tenantBrands]);

  const brandCategories = useMemo(() => {
    if (!tenantBrands || tenantBrands.length === 0) return {};
    const merged: {
      [brandName: string]: {
        available: ArticleCategory[];
        selected: string[];
      };
    } = {};
    tenantBrands.forEach((brand) => {
      const available = allBrandCategories[brand.subdomain] ?? [];
      merged[brand.subdomain] = {
        available,
        selected: selection[brand.subdomain] ?? [],
      };
    });
    return merged;
  }, [tenantBrands, allBrandCategories, allSelectedCategories, selection]);

  const updateBrandSelectedCategories = useCallback(
    (brandName: string, selectedIds: string[]) => {
      setSelection((prev) => {
        return {
          ...prev,
          [brandName]: selectedIds,
        };
      });
    },
    []
  );

  const updateCategoriesMutation = useMutation({
    mutationFn: async (request: ZendeskConfigurationRequest) => {
      return updateCategories(request);
    },
    onError: () => {
      toast.error("Failed to save configuration. Please try again.");
    },
  });
  const handleSaveConfiguration = useCallback(async () => {
    setIsSaving(true);

    const savePromises = Object.entries(selection).map(([brand, selected]) => {
      return updateCategoriesMutation.mutateAsync({
        source,
        brand,
        categories: allBrandCategories[brand]
          .filter((category) => selected.includes(category.id))
          .map((category) => ({
            id: String(category.id),
            name: category.name,
          })),
      });
    });

    const results = await Promise.allSettled(savePromises);
    if (!results.some((result) => result.status === "rejected")) {
      toast.success("saved configuration successfully.");
    }
  }, [
    selection,
    updateCategoriesMutation,
    onClose,
    source,
    allBrandCategories,
  ]);

  const isLoading =
    isLoadingBrands || isLoadingCategories || isLoadingSelectedCategories;
  return {
    tenantBrands,
    brandCategories,
    updateBrandSelectedCategories,
    isSaving,
    isLoading,
    handleSaveConfiguration,
    selection,
  };
}
